import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { useSupportLineTermsApi } from "../../../api/support-line-api";
import Button from "../../Button/Button";
import Checkbox from "../../Checkbox/Checkbox";
import Icon from "../../Icon";
import {
  downloadTermsPdf,
  formatTermsDate,
  TERMS_TITLE,
} from "./generateTermsPdf";
import styles from "./SupportLineTerms.module.css";

export const TERMS_ACKNOWLEDGEMENT =
  "I acknowledge that I have read and reviewed the SupportLine Terms and Conditions and, by electronically accepting them, agree on behalf of the Company to be legally bound by their terms.";
export const TERMS_AUTHORIZATION =
  "I represent and warrant that I am either the Company's legal representative or otherwise authorized to accept these Terms and Conditions on behalf of, and to legally bind, the Company.";

// Tolerates sub-pixel rounding of scrollTop on zoomed displays.
const SCROLL_END_TOLERANCE_PX = 2;

type SupportLineTermsProps = {
  customer: string;
  rootEquipment: string;
  isSubmitting: boolean;
  onBack: () => void;
  onAccept: (version: string) => void;
};

const SupportLineTerms = ({
  customer,
  rootEquipment,
  isSubmitting,
  onBack,
  onAccept,
}: SupportLineTermsProps) => {
  const {
    data: terms,
    isLoading,
    isError,
  } = useSupportLineTermsApi(customer, rootEquipment);
  const [reachedEnd, setReachedEnd] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [authorized, setAuthorized] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const checkReachedEnd = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (
      el.scrollTop + el.clientHeight >=
      el.scrollHeight - SCROLL_END_TOLERANCE_PX
    ) {
      setReachedEnd(true);
    }
  }, []);

  // Short terms that need no scrolling count as read.
  useLayoutEffect(() => {
    if (terms) checkReachedEnd();
  }, [terms, checkReachedEnd]);

  const onDownload = async () => {
    if (!terms) return;
    setIsDownloading(true);
    try {
      await downloadTermsPdf(terms);
    } catch (err) {
      console.error("Terms and Conditions PDF generation failed", err);
    } finally {
      setIsDownloading(false);
    }
  };

  const canAccept =
    !!terms && reachedEnd && acknowledged && authorized && !isSubmitting;

  return (
    <div className={styles.terms}>
      <div className={styles.content}>
        <p className={styles.title}>Accept terms to submit your ticket</p>

        {isLoading && <p className={styles.subtitle}>Loading terms...</p>}
        {isError && (
          <p className={styles.error}>
            Unable to load the Terms and Conditions. Go back and try again.
          </p>
        )}

        {terms && (
          <>
            <p className={styles.subtitle}>
              {`${TERMS_TITLE}, version ${terms.version}, valid from ${formatTermsDate(terms.validFrom)}. `}
              <button
                type="button"
                className={styles["download-link"]}
                onClick={onDownload}
                disabled={isDownloading}
              >
                Download PDF
              </button>
            </p>

            <div
              ref={scrollRef}
              className={styles["terms-box"]}
              onScroll={checkReachedEnd}
              tabIndex={0}
              aria-label={TERMS_TITLE}
              data-testid="terms-scroll"
            >
              <div className={styles["terms-list"]}>
                {terms.sections.map((section, index) => (
                  <section key={index}>
                    <p className={styles["section-title"]}>
                      {`${index + 1}. ${section.title}`}
                    </p>
                    <p className={styles["section-body"]}>{section.body}</p>
                  </section>
                ))}
              </div>
            </div>

            <div
              className={`${styles.status} ${reachedEnd ? styles["status-done"] : styles["status-pending"]}`}
              role="status"
            >
              <Icon
                name={reachedEnd ? "Check_mark" : "Down_arrow"}
                className={styles["status-icon"]}
              />
              <p>
                {reachedEnd
                  ? "You’ve reached the end of the terms."
                  : "Read to the end of the terms to unlock the checkboxes."}
              </p>
            </div>

            <div
              className={`${styles.checkboxes} ${reachedEnd ? "" : styles["checkboxes-disabled"]}`}
            >
              <Checkbox
                text={TERMS_ACKNOWLEDGEMENT}
                checked={acknowledged}
                setChecked={setAcknowledged}
                disabled={!reachedEnd}
              />
              <Checkbox
                text={TERMS_AUTHORIZATION}
                checked={authorized}
                setChecked={setAuthorized}
                disabled={!reachedEnd}
              />
            </div>

            <div className={styles.info}>
              <Icon
                name="Information_circle_2"
                className={styles["info-icon"]}
              />
              <p>
                Not authorized? Ask someone at your company who is allowed to
                sign agreements to log in and accept.
              </p>
            </div>
          </>
        )}
      </div>

      <div className={styles.actions}>
        <Button variant="ghost" text="Back to ticket" onClick={onBack} />
        <Button
          variant="primary"
          text={isSubmitting ? "Submitting..." : "Accept and submit ticket"}
          onClick={() => terms && onAccept(terms.version)}
          disabled={!canAccept}
        />
      </div>
    </div>
  );
};

export default SupportLineTerms;
